import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { HybridRepository } from '@cogo/kg-repository'
import { useStudioStore } from '@/store/studio-store'
import { useKg } from '../kg-provider'
import {
  VOCABULARY_ENUMS,
  type EnumCount,
  type EnumCounts,
  type VocabularyEnumKey,
  type VocabularyItem,
} from './types'
import { EnumNav } from './enum-nav'
import { VocabularyTable } from './vocabulary-table'
import {
  VocabularyLoading,
  VocabularyUnavailable,
  VocabularyError,
} from './vocabulary-states'
import { filterItems } from './vocabulary-search'

// Vocabulary reads go through the Repository (kg-client bypass migrated to
// @cogo/kg-repository). A null repo (flag off / init failed) renders the
// Unavailable state, matching the former localGet* null contract.
const QUERY_BY_ENUM: Record<
  VocabularyEnumKey,
  (repo: HybridRepository) => Promise<VocabularyItem[]>
> = {
  prefix: async (repo) => (await repo.getExpressionPrefixes()).data,
  accessor: async (repo) => (await repo.getExpressionAccessors()).data,
  event: async (repo) => (await repo.getUiEventVocabulary()).data,
  action: async (repo) => (await repo.getUiActionVocabulary()).data,
}

/**
 * One enum's slot: the rows it returned, or why it has none yet.
 *
 * The nav's badge is derived from THIS — `count` is `items.length` of the
 * very array the table renders — so the number beside a label and the rows
 * behind it cannot drift apart. The panel used to keep a separate counts map
 * that only the visited tab ever wrote, which is why three of four counters
 * sat at "…" until clicked.
 */
type EnumState =
  | { kind: 'pending' }
  | { kind: 'ready'; items: VocabularyItem[] }
  | { kind: 'error'; error: Error }

type PanelState = Record<VocabularyEnumKey, EnumState>

/**
 * Derived from VOCABULARY_ENUMS rather than hand-listed, so a fifth
 * descriptor gets a slot, a query and a counter by existing — instead of
 * sitting at "…" forever because a parallel map went unextended.
 */
function allPending(): PanelState {
  return Object.fromEntries(
    VOCABULARY_ENUMS.map((descriptor) => [descriptor.key, { kind: 'pending' }]),
  ) as PanelState
}

function toCounts(states: PanelState): EnumCounts {
  return Object.fromEntries(
    VOCABULARY_ENUMS.map((descriptor) => {
      const state = states[descriptor.key]
      const count: EnumCount =
        state.kind === 'ready'
          ? { kind: 'ready', count: state.items.length }
          : state.kind === 'error'
            ? { kind: 'error' }
            : { kind: 'pending' }
      return [descriptor.key, count]
    }),
  ) as EnumCounts
}

export function VocabularyPanel() {
  const { ready, flagEnabled, repo } = useKg()
  const activeEnum = useStudioStore((s) => s.vocabularyActiveEnum)
  const setActiveEnum = useStudioStore((s) => s.setVocabularyActiveEnum)

  const [searchQuery, setSearchQuery] = useState('')
  const [states, setStates] = useState<PanelState>(allPending)

  // Bumped whenever a fresh load starts; in-flight queries from an older
  // repo instance check it before writing, so a swapped repo cannot have its
  // slots overwritten by the previous one's late answers.
  const epochRef = useRef(0)

  // Reset search when enum changes
  useEffect(() => {
    setSearchQuery('')
  }, [activeEnum])

  /**
   * Load ONE enum into its own slot.
   *
   * Each enum settles independently: the failure path writes only its own
   * key, so a rejected query cannot blank the counters that did come back.
   * That is structural, not a convention to remember.
   */
  const loadEnum = useCallback(
    async (key: VocabularyEnumKey, repository: HybridRepository, epoch: number) => {
      setStates((s) => ({ ...s, [key]: { kind: 'pending' } }))
      try {
        const items = await QUERY_BY_ENUM[key](repository)
        if (epochRef.current !== epoch) return
        setStates((s) => ({ ...s, [key]: { kind: 'ready', items } }))
      } catch (err) {
        if (epochRef.current !== epoch) return
        setStates((s) => ({
          ...s,
          [key]: { kind: 'error', error: err instanceof Error ? err : new Error(String(err)) },
        }))
      }
    },
    [],
  )

  // All four load on mount (and on any ready/repo transition). The store is
  // already hydrated in memory by the time `ready` flips, so these are four
  // local SPARQL SELECTs against it — and a reader who browses the panel
  // would have run all four anyway, one tab click at a time.
  useEffect(() => {
    if (!ready) return
    if (!flagEnabled || !repo) return
    const epoch = epochRef.current + 1
    epochRef.current = epoch
    setStates(allPending())
    for (const descriptor of VOCABULARY_ENUMS) {
      void loadEnum(descriptor.key, repo, epoch)
    }
  }, [ready, flagEnabled, repo, loadEnum])

  const counts = useMemo(() => toCounts(states), [states])
  const active = states[activeEnum]

  const retryActive = useCallback(() => {
    if (!repo) return
    void loadEnum(activeEnum, repo, epochRef.current)
  }, [activeEnum, repo, loadEnum])

  if (!ready) return <VocabularyLoading />
  if (!flagEnabled || !repo) {
    return panelWithNav(activeEnum, setActiveEnum, counts, searchQuery, setSearchQuery, <VocabularyUnavailable />)
  }
  if (active.kind === 'error') {
    return panelWithNav(activeEnum, setActiveEnum, counts, searchQuery, setSearchQuery, <VocabularyError error={active.error} onRetry={retryActive} />)
  }
  if (active.kind === 'pending') {
    return panelWithNav(activeEnum, setActiveEnum, counts, searchQuery, setSearchQuery, <VocabularyLoading />)
  }

  const filtered = filterItems(active.items, searchQuery)

  return panelWithNav(
    activeEnum,
    setActiveEnum,
    counts,
    searchQuery,
    setSearchQuery,
    <VocabularyTable
      enumKey={activeEnum}
      items={filtered}
      totalBeforeFilter={active.items.length}
      searchQuery={searchQuery}
      onClearSearch={() => setSearchQuery('')}
    />,
  )
}

function panelWithNav(
  activeEnum: VocabularyEnumKey,
  setActiveEnum: (k: VocabularyEnumKey) => void,
  counts: EnumCounts,
  searchQuery: string,
  setSearchQuery: (q: string) => void,
  rightContent: React.ReactNode,
) {
  return (
    <div className="flex h-full">
      <EnumNav
        activeEnum={activeEnum}
        counts={counts}
        searchQuery={searchQuery}
        onSelectEnum={setActiveEnum}
        onSearchChange={setSearchQuery}
      />
      <div className="flex-1 overflow-hidden">{rightContent}</div>
    </div>
  )
}
